import type { FilterCondition } from '../../packages/shared-datagrid/src';

export const errorMsg = "Please fill out this field or select N/A if it's not applicable.";

export enum StatusOptions {
  Draft = 'Draft',
  Submitted = 'Submitted',
  ReviewNeeded = 'Review Needed',
  ConsultRequested = 'Consult Requested',
  WaiverPending = 'Waiver Pending',
  Rejected = 'Rejected',
  Approved = 'Approved',
  ConsultInfoNeeded = 'Consult Info Needed',
  ReturnedToSubmitter = 'Returned to Submitter',
}

export enum FormStatusIdOptions {
  Draft = 1,
  Submitted = 2,
  ReviewNeeded = 3,
  ConsultRequested = 4,
  WaiverPending = 5,
  Rejected = 6,
  Approved = 7,
  ConsultInfoNeeded = 8,
  ReturnedToSubmitter = 9,
}

export const STATUS_OPTIONS = [
  StatusOptions.Draft,
  StatusOptions.Submitted,
  StatusOptions.ReviewNeeded,
  StatusOptions.ConsultRequested,
  StatusOptions.ConsultInfoNeeded,
  StatusOptions.WaiverPending,
  StatusOptions.Approved,
  StatusOptions.Rejected,
  StatusOptions.ReturnedToSubmitter,
];

export const ADMIN_DELETABLE_STATUSES = [
  StatusOptions.Draft,
  StatusOptions.Submitted,
  StatusOptions.ReviewNeeded,
  StatusOptions.ConsultRequested,
  StatusOptions.WaiverPending,
  StatusOptions.ConsultInfoNeeded,
  StatusOptions.ReturnedToSubmitter,
];

/** Statuses considered final; forms in these statuses are locked from further edits/review. */
export const TERMINAL_FORM_STATUSES: readonly StatusOptions[] = [
  StatusOptions.Approved,
  StatusOptions.Rejected,
];

/**
 * A missing status (e.g. not yet loaded) is treated as terminal so consumers
 * fail safe and don't allow edits/approvals before the real status resolves.
 */
export const isTerminalFormStatus = (status: string | null | undefined): boolean =>
  !status || TERMINAL_FORM_STATUSES.includes(status as StatusOptions);

export const SERVICE_GROUP_OPTIONS = [
  'Assurance',
  'Tax',
  'Outsourcing',
  'Digital',
  'Enterprise Services',
];

export const SERVICE_LINE_OPTIONS = [
  // Assurance Service Lines
  'Agreed Upon Procedures',
  'Assurance Related Consulting',
  'Attest Services',
  'Audit',
  'Compilation',
  'Global Services - Assurance',
  'Peer Review Services',
  'Preparation',
  'Prospective Financial Information',
  'Review',
  'SEC',
  // Consulting Service Lines
  'Federal/Government',
  'Financial Institutions',
  'Health Care Consulting',
  'Industry/Business Consulting',
  'Retirement Plan Services',
  // Digital Service Lines
  'Digital - Cybersecurity',
  'Digital - Reseller',
  'Digital - Services',
  // Outsourcing Service Lines
  'Biz Ops',
  'CAST',
  'Spec Industry Specific',
  'Special Adv Svcs',
  'Talent Solutions',
  'Value & Risk Services',
  // Tax Service Lines
  'Business Incentives Consulting',
  'Business Tax',
  'Federal Tax Strategies',
  'Global Tax',
  'Individuals & Trusts',
  'State & Local Tax (SALT)',
  'Tax Exempts & Qualified Plans',
  'Tax Seminars/ Publications',
  'Transaction Tax',
];

export const FILTER_CLEAR_DELAY = 500;
export const SEARCH_DEBOUNCE = 300;

export const ADMIN_SUBMENU_LABELS = ['COI Form', 'Manage Quality Leaders', 'Email Types'];

export const CHARACTER_LIMIT = {
  FiveThousand: 5000,
  FiveHundred: 500,
  OneHundredFifty: 150,
};

export const DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
  timeZone: 'America/Chicago',
});
export const TIME_FORMAT = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'America/Chicago',
});

const createStatusFilter = (status: StatusOptions): FilterCondition => ({
  column: 'status',
  operator: 'equal',
  value: status,
  type: 'text',
});

export const STATUS_FILTERS = {
  reviewNeeded: createStatusFilter(StatusOptions.ReviewNeeded),
  consultRequested: createStatusFilter(StatusOptions.ConsultRequested),
  waiverPending: createStatusFilter(StatusOptions.WaiverPending),
  consultInfoNeeded: createStatusFilter(StatusOptions.ConsultInfoNeeded),
  rejected: createStatusFilter(StatusOptions.Rejected),
  approved: createStatusFilter(StatusOptions.Approved),
  returnedToSubmitter: createStatusFilter(StatusOptions.ReturnedToSubmitter),
} as const;

export const ADMIN_SUBMENU_ROUTES: Record<string, string> = {
  'Manage Quality Leaders': '/adminview/managequalityleaders',
  'Email Types': '/adminview/emailtypes',
};

const EMAIL_TEMPLATE_SHARED_BODY =
  '<p>Hello,</p><br/><p>The COI form ID [FORM_URL] submitted for review in the Conflicts of Interest (COI) tool for [ACCOUNT_NAME] - [ACCOUNT_NUMBER] has been [FORM_STATUS].</p><br/><p>Approver Comments: [APPROVER_COMMENTS]</p><br/><p>*Please visit the form above to view details as needed.</p><br/><p>Regards,</p><p>Conflicts of Interest Leadership Team</p>';

const EMAIL_TEMPLATE_SHARED_FORM_TYPE = {
  label: 'COI',
  value: 1,
} as const;

export const EMAIL_TEMPLATE_BY_STATUS = {
  [StatusOptions.Rejected]: {
    name: 'Rejected COI Form Template',
    subject: 'COI Form Rejected for [ACCOUNT_NAME]',
    body: EMAIL_TEMPLATE_SHARED_BODY,
    formType: EMAIL_TEMPLATE_SHARED_FORM_TYPE,
    status: {
      label: StatusOptions.Rejected,
      value: FormStatusIdOptions.Rejected,
    },
  },
  [StatusOptions.Approved]: {
    name: 'Approved COI Form Template',
    subject: 'COI Form Approved for [ACCOUNT_NAME]',
    body: EMAIL_TEMPLATE_SHARED_BODY,
    formType: EMAIL_TEMPLATE_SHARED_FORM_TYPE,
    status: {
      label: StatusOptions.Approved,
      value: FormStatusIdOptions.Approved,
    },
  },
} as const;

export const DEFAULT_EMAIL_TEMPLATE_STATUS = StatusOptions.Rejected;

export const EMAIL_TEMPLATE_TOAST_MESSAGES = {
  saveSuccess: 'Email template saved successfully.',
  updateSuccess: 'Email template updated successfully.',
  saveFailed: 'Failed to save email template. Please try again.',
  testEmailSuccess: 'Test email has been sent successfully.',
  testEmailFailed: 'Test email could not be sent.',
  testEmailNotImplemented: 'Test email sending is not implemented yet.',
} as const;

export const TRIGGER_EMAIL_OPTIONS = [
  { label: 'Daily', value: 'DAILY' },
  { label: 'On Demand', value: 'ON_DEMAND' },
  { label: 'Escalation', value: 'ESCALATION' },
];

export const EMAIL_TEMPLATE_GRID_TEXT = {
  createNewTemplate: 'Create New Template',
  statusActive: 'Active',
  statusInactive: 'Inactive',
  setActive: 'Set Active',
  setInactive: 'Set Inactive',
  confirmationTitle: 'Confirmation',
  confirmationMessage:
    'There is already active with the same Status & Type. Are you sure you want to set this template to Active? The email template type will replace the prior Active version, making it Active. Ready?',
  confirmationYes: 'Yes',
  confirmationNo: 'No',
  activeTemplateConflictMessage:
    'An active email template for the same form type and form status already exists.',
  updateFailed: 'Failed to update template status. Please try again.',
  defaultTemplateLabel: 'email template',
  openActionsFor: 'Open actions for',
  columnHeaders: {
    subject: 'Subject',
    formType: 'Form Type',
    name: 'Template Name',
    status: 'Status',
    createdOn: 'Created On',
    triggerName: 'Trigger Name',
  },
} as const;
